import { StatusBar } from 'expo-status-bar';
import { useAtom } from 'jotai';
import * as React from 'react';
import { type ReactNode, useState } from 'react';
import { LayoutAnimation } from 'react-native';
import {
    Accordion,
    BodySmallRegular,
    ChevronDownIcon,
    CloseButton,
    H2Regular,
    H6Regular,
    Icon,
    isNonMobileWeb,
    ScrollView,
    SimpleModal,
    Square,
    View,
    XStack,
    YStack,
} from 'smoothie';

import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { useShowGoalsExplanation } from '../../hooks/useShowGoalsExplanation';
import { goalsExplanationSimpleModalIsOpenAtom } from '../../state/GoalAtoms';
import {
    GoalCategoryDescription,
    GoalColors,
    GoalIcons,
    GoalSecondaryColors,
} from '../../types/Goal';

const GOAL_CATEGORIES = [
    {
        title: 'Fundamentals',
        description:
            "We'll help you come up with a plan to deal with any debt that might be holding you back from leveling up. We'll also ensure you've got an emergency fund established and are earning top interest rates on those savings!",
        color: GoalColors[GoalCategoryDescription.FUNDAMENTALS],
        icon: (
            <Icon
                icon={GoalIcons[GoalCategoryDescription.FUNDAMENTALS]}
                size={19}
                color={
                    GoalSecondaryColors[GoalCategoryDescription.FUNDAMENTALS]
                }
            />
        ),
    },
    {
        title: 'Money Management',
        description:
            "Figuring out where your money's going shouldn't stress you out. We'll help you set up a simple, sustainable system to ensure everything from your bills to your goals are taken care of.",
        color: GoalColors[GoalCategoryDescription.MONEY_MANAGEMENT],
        icon: (
            <Icon
                icon={GoalIcons[GoalCategoryDescription.MONEY_MANAGEMENT]}
                size={19}
                color={
                    GoalSecondaryColors[
                        GoalCategoryDescription.MONEY_MANAGEMENT
                    ]
                }
            />
        ),
    },
    {
        title: 'Building Wealth',
        description:
            'Your money should be working for you, not the other way around. We’ll build and implement a personalized investment approach tailored to your goals and timelines.',
        color: GoalColors[GoalCategoryDescription.BUILDING_WEALTH],
        icon: (
            <Icon
                icon={GoalIcons[GoalCategoryDescription.BUILDING_WEALTH]}
                size={19}
                color={
                    GoalSecondaryColors[GoalCategoryDescription.BUILDING_WEALTH]
                }
            />
        ),
    },
    {
        title: 'Milestones',
        description:
            'Whether it’s combining finances, buying a house, making a career change, planning for kids, traveling, or a dozen other things, we’ll help make sure you have a plan in place for hitting those goals.',
        color: GoalColors[GoalCategoryDescription.MILESTONES],
        icon: (
            <Icon
                icon={GoalIcons[GoalCategoryDescription.MILESTONES]}
                size={19}
                color={GoalSecondaryColors[GoalCategoryDescription.MILESTONES]}
            />
        ),
    },
    {
        title: 'Legacy',
        description:
            "Safety first! We'll make sure all your financial bases are covered. That means setting you up with essential documents and appropriate insurance to protect you and your family's future.",
        color: GoalColors[GoalCategoryDescription.LEGACY],
        icon: (
            <Icon
                icon={GoalIcons[GoalCategoryDescription.LEGACY]}
                size={19}
                color={GoalSecondaryColors[GoalCategoryDescription.LEGACY]}
            />
        ),
    },
];

interface AccordionItemProps {
    title: string;
    description: string;
    color: string;
    icon: ReactNode;
    isOpen: boolean;
}

const AccordionItem = ({
    title,
    description,
    color,
    icon,
    isOpen,
}: AccordionItemProps) => {
    const backgroundColor = isOpen ? color : '$gray100';

    return (
        <Accordion.Item key={title} value={title} borderWidth={0}>
            <Accordion.Trigger
                borderRadius={12}
                borderTopWidth={0}
                borderBottomWidth={0}
                borderLeftWidth={0}
                borderRightWidth={0}
                backgroundColor={backgroundColor}
                pressStyle={{ backgroundColor }}
                focusStyle={{ backgroundColor }}
            >
                <YStack>
                    <XStack
                        flexDirection="row"
                        alignItems="center"
                        marginBottom={isOpen ? 12 : 0}
                        gap={12}
                    >
                        {icon}
                        <H6Regular>{title}</H6Regular>
                        <Square
                            animation="tooltip"
                            rotate={isOpen ? '180deg' : '0deg'}
                            marginLeft="auto"
                        >
                            <Icon
                                icon={ChevronDownIcon}
                                size={20}
                                color="$gray1200"
                            />
                        </Square>
                    </XStack>
                    {isOpen && (
                        <BodySmallRegular>{description}</BodySmallRegular>
                    )}
                </YStack>
            </Accordion.Trigger>
        </Accordion.Item>
    );
};

const GoalCategoriesAccordion = () => {
    const [openItem, setOpenItem] = useState<string | null>(null);

    const toggleItem = (val: string) => {
        LayoutAnimation.configureNext(
            LayoutAnimation.create(300, 'easeInEaseOut', 'opacity'),
        );
        setOpenItem(val);
    };

    return (
        <Accordion
            overflow="hidden"
            type="single"
            rowGap={14}
            onValueChange={(val) => {
                toggleItem(val);
            }}
            collapsible={true}
        >
            {GOAL_CATEGORIES.map((goalCategory) => {
                const { title, description, color, icon } = goalCategory;

                return (
                    <AccordionItem
                        key={title}
                        title={title}
                        description={description}
                        color={color}
                        icon={icon}
                        isOpen={openItem === title}
                    />
                );
            })}
        </Accordion>
    );
};

const GoalsExplanationModal = () => {
    LogRocketFacade.tagPage('GoalsExplanationModal');
    const showGoalsExplanationModal = useShowGoalsExplanation();

    return (
        <View height="100%" maxWidth={700}>
            <StatusBar style="light" />
            <ScrollView
                backgroundColor="$white"
                showsVerticalScrollIndicator={false}
            >
                <XStack justifyContent="flex-end">
                    <CloseButton
                        onPress={() => {
                            showGoalsExplanationModal(false);
                        }}
                        background="gray"
                    />
                </XStack>
                <YStack
                    gap={24}
                    padding={isNonMobileWeb ? 16 : 24}
                    paddingTop={0}
                >
                    <H2Regular>What are goals?</H2Regular>
                    <YStack gap={8}>
                        <BodySmallRegular marginBottom={8} color="$gray1000">
                            Introduction
                        </BodySmallRegular>
                        <BodySmallRegular>
                            Financial goals can be short, medium or long term.
                            These goals can help you succeed in your personal
                            and professional life and save for retirement.
                            Examples of financial goals include creating an
                            emergency savings account, building a retirement
                            fund, paying off debt and finding a higher-paying
                            job.
                        </BodySmallRegular>
                    </YStack>
                    <YStack gap={8}>
                        <BodySmallRegular color="$gray1000">
                            Categories
                        </BodySmallRegular>
                        <GoalCategoriesAccordion />
                    </YStack>
                </YStack>
            </ScrollView>
        </View>
    );
};

export const GoalsExplanationSimpleModal = () => {
    const [
        goalsExplanationSimpleModalIsOpen,
        setGoalsExplanationSimpleModalIsOpen,
    ] = useAtom(goalsExplanationSimpleModalIsOpenAtom);

    return (
        <SimpleModal
            isOpen={goalsExplanationSimpleModalIsOpen}
            onClose={() => {
                setGoalsExplanationSimpleModalIsOpen(false);
            }}
        >
            <GoalsExplanationModal />
        </SimpleModal>
    );
};

export default GoalsExplanationModal;
